"use client";

import { Button } from "@/components/shadcn/button";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { Separator } from "@/components/shadcn/separator";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/shadcn/sheet";
import { Filter } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useFilterParams } from "./filter-utils";

type Option = { value: string; label: string };

export type CategoryOption = { Id: number; Description: string };

const TITLE_OPTIONS: Option[] = [
    { value: "GM", label: "GM" },
    { value: "IM", label: "IM" },
    { value: "FM", label: "FM" },
    { value: "CM", label: "CM" },
];

const parseCsv = (value: string | null) =>
    value ? value.split(",").filter(Boolean) : [];

function CheckboxListSection({
    title,
    idPrefix,
    options,
    selected,
    onToggle,
}: {
    title: string;
    idPrefix: string;
    options: Option[];
    selected: string[];
    onToggle: (value: string) => void;
}) {
    return (
        <section className="space-y-3">
            <p className="text-muted-foreground text-xs">{title}</p>
            <div className="rounded-lg border bg-white">
                {options.map((option, i) => (
                    <Label
                        key={option.value}
                        htmlFor={`${idPrefix}-${option.value}`}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-3 font-normal ${
                            i > 0 ? "border-t" : ""
                        }`}
                    >
                        <Checkbox
                            id={`${idPrefix}-${option.value}`}
                            checked={selected.includes(option.value)}
                            onCheckedChange={() => onToggle(option.value)}
                        />
                        {option.label}
                    </Label>
                ))}
            </div>
        </section>
    );
}

const toggleValue = (values: string[], value: string) =>
    values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value];

export default function FiltersDrawer({
    availableCategories,
}: {
    availableCategories: CategoryOption[];
}) {
    const t = useTranslations("seriesFilters");
    const { searchParams, setParams } = useFilterParams();

    const categoryOptions: Option[] = availableCategories.map((category) => ({
        value: String(category.Id),
        label: category.Description,
    }));

    const [open, setOpen] = useState(false);
    const [titles, setTitles] = useState<string[]>(() =>
        parseCsv(searchParams.get("titles")),
    );
    const [categories, setCategories] = useState<string[]>(() =>
        parseCsv(searchParams.get("categories")),
    );
    const [rating, setRating] = useState(
        () => searchParams.get("rating") ?? "",
    );

    const activeCount =
        (titles.length > 0 ? 1 : 0) +
        (categories.length > 0 ? 1 : 0) +
        (rating ? 1 : 0);

    const handleOpenChange = (next: boolean) => {
        if (next) {
            setTitles(parseCsv(searchParams.get("titles")));
            setCategories(parseCsv(searchParams.get("categories")));
            setRating(searchParams.get("rating") ?? "");
        }
        setOpen(next);
    };

    const handleClear = () => {
        setTitles([]);
        setCategories([]);
        setRating("");
    };

    const handleApply = () => {
        setParams({
            titles: titles.length > 0 ? titles.join(",") : null,
            categories: categories.length > 0 ? categories.join(",") : null,
            rating: rating.trim() || null,
        });
        setOpen(false);
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetTrigger asChild>
                <Button variant="outline">
                    <Filter className="size-4" />
                    {t("filter")}
                </Button>
            </SheetTrigger>
            <SheetContent className="flex flex-col gap-0 p-0 sm:max-w-sm">
                <SheetHeader className="relative border-b px-6 py-4">
                    <SheetTitle className="text-center">
                        {t("filter")}
                    </SheetTitle>
                    {activeCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            className="absolute top-1/2 left-2 h-8 -translate-y-1/2"
                        >
                            {t("clear")}
                        </Button>
                    )}
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    <CheckboxListSection
                        title={t("titleSection")}
                        idPrefix="title"
                        options={TITLE_OPTIONS}
                        selected={titles}
                        onToggle={(value) =>
                            setTitles((prev) => toggleValue(prev, value))
                        }
                    />

                    <Separator className="my-6" />

                    <CheckboxListSection
                        title={t("categoriesSection")}
                        idPrefix="category"
                        options={categoryOptions}
                        selected={categories}
                        onToggle={(value) =>
                            setCategories((prev) => toggleValue(prev, value))
                        }
                    />

                    <Separator className="my-6" />

                    <section className="space-y-3">
                        <Label
                            htmlFor="rating"
                            className="text-muted-foreground text-xs font-normal"
                        >
                            {t("level")}
                        </Label>
                        <div className="rounded-lg border bg-white px-3 py-2">
                            <Input
                                id="rating"
                                type="number"
                                inputMode="numeric"
                                placeholder="1800"
                                value={rating}
                                onChange={(e) => setRating(e.target.value)}
                                className="h-auto border-0 p-0 text-base focus-visible:ring-0"
                            />
                        </div>
                    </section>
                </div>

                <SheetFooter className="grid grid-cols-2 gap-3 border-t p-4 sm:flex-row sm:space-x-0">
                    <SheetClose asChild>
                        <Button variant="outline">{t("cancel")}</Button>
                    </SheetClose>
                    <Button onClick={handleApply}>
                        {t("apply")}
                        {activeCount > 0 ? ` (${activeCount})` : ""}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
